import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAttendanceShiftNav } from "../../hooks/useAttendanceShiftNav";
import { useOtHoursAllocationNav } from "../../hooks/useOtHoursAllocationNav";
import logo1 from "../assets/images/brixigo-b-logo.png";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { hasRole, normalizeRole, R } from "../../rbac/access";
import { filterSidebarByPageAccess } from "../../services/pageAccessService";
import { useFeatures } from "../../Moduels/Subscription/FeaturesContext";
import { EMPLOYEE_SIDEBAR_CONFIG } from "../../config/employeeWorkspaceNav";
import { SETTINGS_CATEGORIES, filterSettingsHubItems } from "../../config/settingsNavigation";
import { isSettingsPath, consumeSettingsReturnPath } from "../settings/settingsExit";
import { clearPipelineState } from "../../Moduels/Salary/v2/enterprise/components/salaryTemplatePipeline";
import {
  Home,
  Users,
  Wallet,
  Briefcase,
  CheckCircle,
  Calendar,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Layers,
  Shield,
  FileText,
  Upload,
  UserCheck,
  ChevronDown,
  Zap,
  Bell,
  X,
  Settings,
  Search,
  BarChart3,
  Banknote,
  Receipt,
  Calculator,
  Clock,
  Lock,
  AlertTriangle,
  Activity,
  Database,
  History,
  Building2,
  UserCog,
  CalendarDays,
  CalendarOff,
  ClipboardList,
  GitBranch,
  Sparkles,
  FunctionSquare,
  Network,
  MapPin,
  Hash,
  LayoutDashboard,
  Repeat,
  ArrowLeft,
  Fuel,
} from "lucide-react";

const ICONS = {
  home: Home,
  users: Users,
  wallet: Wallet,
  briefcase: Briefcase,
  "check-circle": CheckCircle,
  calendar: Calendar,
  "help-circle": HelpCircle,
  layers: Layers,
  shield: Shield,
  "file-text": FileText,
  upload: Upload,
  "user-check": UserCheck,
  zap: Zap,
  bell: Bell,
  settings: Settings,
  "bar-chart": BarChart3,
  banknote: Banknote,
  receipt: Receipt,
  calculator: Calculator,
  clock: Clock,
  lock: Lock,
  alert: AlertTriangle,
  activity: Activity,
  database: Database,
  history: History,
  building: Building2,
  "user-cog": UserCog,
  "calendar-days": CalendarDays,
  "calendar-off": CalendarOff,
  clipboard: ClipboardList,
  "git-branch": GitBranch,
  sparkles: Sparkles,
  repeat: Repeat,
  "function-square": FunctionSquare,
  network: Network,
  "map-pin": MapPin,
  hash: Hash,
  "layout-dashboard": LayoutDashboard,
  fuel: Fuel,
};

// SIDEBAR_CONFIG - Professionally arranged navigation
// Order: Overview, People, Time, Leave, Payroll, Banking,
//        Compliance, Reports, Manager, Self, Admin, Resources
export const SIDEBAR_CONFIG = [
  // 1. Overview
  {
    section: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard", icon: "home", path: "/dashboard", color: "ic-blue" },
    ],
  },

  // 2. People Management — unified hub
  {
    section: "People",
    roles: ["admin", "hr", "manager", "finance"],
    items: [
      {
        key: "people_hub",
        label: "People Hub",
        icon: "users",
        path: "/employees",
        color: "ic-blue",
        roles: ["admin", "hr", "manager", "finance"],
      },
    ],
  },

  // -- 3. Attendance (MVP) --
  {
    section: "Attendance",
    roles: ["admin", "hr"],
    items: [
      {
        key: "att_mvp",
        label: "Attendance",
        icon: "calendar",
        color: "ic-blue",
        roles: ["admin", "hr"],
        children: [
          { key: "att_dash", label: "Attendance Dashboard", icon: "bar-chart", path: "/attendance/dashboard", color: "ic-blue" },
          { key: "att_mark", label: "Mark Attendance", icon: "check-circle", path: "/attendance/mark", color: "ic-green", roles: ["admin", "hr"] },
          { key: "att_records", label: "Attendance Records", icon: "clipboard", path: "/attendance/records", color: "ic-blue" },
          { key: "att_cal", label: "Attendance Calendar", icon: "calendar-days", path: "/attendance/apply-calendar", color: "ic-blue" },
          {
            key: "att_ot_hours",
            label: "Overtime",
            icon: "clock",
            path: "/attendance/ot-hours",
            color: "ic-indigo",
            roles: ["admin", "hr"],
            requiresOtHoursAllocation: true,
          },
          { key: "att_holiday", label: "Holiday Calendar", icon: "calendar", path: "/holiday-calendar", color: "ic-blue" },
          { key: "att_devices", label: "Biometric Devices", icon: "database", path: "/attendance/devices", color: "ic-blue" },
          { key: "att_device_mapping", label: "Device Mapping", icon: "network", path: "/attendance/employee-device-mapping", color: "ic-blue" },
          { key: "att_shifts", label: "Shift Management", icon: "clock", path: "/attendance/shifts", color: "ic-amber" },
        ],
      },
    ],
  },

  // -- 4. Leave Management --
  {
    section: "Leave Management",
    roles: ["admin", "hr", "manager"],
    items: [
      {
        key: "lv_mvp",
        label: "Leave",
        icon: "calendar-off",
        color: "ic-blue",
        roles: ["admin", "hr", "manager"],
        children: [
          { key: "lv_dashboard", label: "Dashboard", icon: "layout-dashboard", path: "/leave", color: "ic-blue" },
          { key: "lv_apply", label: "Apply Leave", icon: "clipboard", path: "/leave?tab=apply", color: "ic-blue", roles: ["hr", "manager"] },
          { key: "lv_assign", label: "Assign Leave", icon: "user-check", path: "/leave?tab=assign", color: "ic-blue", roles: ["admin"] },
          { key: "lv_balance", label: "Balances", icon: "wallet", path: "/leave?tab=balances", color: "ic-blue" },
          { key: "lv_comp_off", label: "Comp Off", icon: "calendar-off", path: "/leave/comp-off", color: "ic-amber" },
          { key: "lv_encashment", label: "Leave Encashment", icon: "wallet", path: "/leave/encashment", color: "ic-blue" },
          { key: "lv_approval", label: "Approve", icon: "check-circle", path: "/leave?tab=approval", color: "ic-green" },
          { key: "lv_analytics", label: "Analytics", icon: "bar-chart", path: "/leave?tab=analytics", color: "ic-blue", roles: ["admin", "hr"] },
        ],
      },
    ],
  },

  // -- 5. Payroll (MVP) --
  {
    section: "Payroll",
    roles: ["admin", "hr"],
    items: [
      {
        key: "pay_mvp",
        label: "Payroll",
        icon: "wallet",
        color: "ic-blue",
        roles: ["admin", "hr"],
        children: [
          {
            key: "pay_process",
            label: "Payroll Hub",
            icon: "zap",
            path: "/payroll-process",
            color: "ic-green",
          },
          { key: "pay_payslips", label: "Payslips", icon: "file-text", path: "/payslip", color: "ic-green" },
          {
            key: "pay_payroll_calendar",
            label: "Payroll Calendar",
            icon: "calendar-days",
            path: "/salary-v2/payroll-calendar",
            color: "ic-green",
          },
        ],
      },
    ],
  },

  // -- 6. Banking & Finance (manager/finance roles) --
  {
    section: "Banking & Finance",
    roles: ["admin", "hr", "finance"],
    items: [
      { key: "approvals", label: "Approvals", icon: "check-circle", path: "/approvals", color: "ic-green", roles: ["admin", "hr"] },
      { key: "loans", label: "Loans & Advances", icon: "briefcase", path: "/loans", color: "ic-amber", roles: ["admin", "hr"], feature: "loan.manage" },
      { key: "fraud", label: "Fraud Console", icon: "shield", path: "/fraud", color: "ic-red", roles: ["admin", "finance"] },
      { key: "events", label: "Events Console", icon: "bell", path: "/events", color: "ic-amber", roles: ["admin", "finance"] },
    ],
  },

  // -- 7. Payments --
  {
    section: "Payments",
    roles: ["admin", "hr", "finance"],
    items: [
      { key: "comp_subscription", label: "Subscription & Plans", icon: "banknote", path: "/settings/subscription", color: "ic-purple", roles: ["admin"] },
      { key: "bank_requests", label: "Bank Payments", icon: "banknote", path: "/bank", color: "ic-blue" },
    ],
  },

  // -- 8. Reports --
  {
    section: "Reports",
    roles: ["admin", "hr"],
    items: [
      { key: "rpt_hub", label: "Reports", icon: "bar-chart", path: "/reports", color: "ic-blue", roles: ["admin", "hr"], collapseOnClick: true },
    ],
  },

  // -- 9. Manager Workspace --
  {
    section: "Manager Workspace",
    roles: ["manager"],
    items: [
      { key: "mgr_leave_approval", label: "Approve Leave", icon: "check-circle", path: "/leave?tab=approval", color: "ic-blue", roles: ["manager"], feature: "leave.manage" },
      { key: "mgr_team_att", label: "Team Attendance", icon: "calendar", path: "/attendance/overview", color: "ic-teal", roles: ["manager"] },
      { key: "mgr_ot_approval", label: "OT Approvals", icon: "zap", path: "/attendance/approvals", color: "ic-amber", roles: ["manager"] },
      { key: "mgr_org_hierarchy", label: "My Team", icon: "users", path: "/org-hierarchy", color: "ic-purple", roles: ["manager"] },
    ],
  },

  // -- Settings --
  {
    section: "Settings",
    roles: ["admin", "hr"],
    items: [
      {
        key: "settings_hub",
        label: "Settings",
        icon: "settings",
        color: "ic-slate",
        path: "/settings",
        roles: ["admin", "hr"],
      },
      { key: "audit_logs", label: "Audit Logs", icon: "history", path: "/audit", color: "ic-red", roles: ["admin"] },
    ],
  },

  // -- 12. Resources --
  {
    section: "Resources",
    items: [
      { key: "notice_board", label: "Notice Board", icon: "bell", path: "/noticeboard", color: "ic-amber" },
      { key: "help", label: "Help Center", icon: "help-circle", path: "/help", color: "ic-slate" },
      { key: "support_center", label: "Support Center", icon: "shield", path: "/support", color: "ic-slate" },
    ],
  },
];

/* ===
   Helpers — validate, dedupe, search, persistence
=== */

function validateSidebar(config) {
  const seenKeys = new Set();
  const duplicates = new Set();
  const walk = (items) => {
    for (const item of items) {
      if (!item?.key) continue;
      if (seenKeys.has(item.key)) {
        duplicates.add(item.key);
        console.warn("Duplicate sidebar key:", item.key);
        continue;
      }
      seenKeys.add(item.key);
      if (Array.isArray(item.children)) walk(item.children);
    }
  };
  for (const section of config) if (Array.isArray(section?.items)) walk(section.items);
  return { duplicates };
}

function filterUniqueSidebar(config) {
  const seenKeys = new Set();
  const filterItems = (items) =>
    items
      .filter((it) => {
        if (!it?.key) return false;
        if (seenKeys.has(it.key)) return false;
        seenKeys.add(it.key);
        return true;
      })
      .map((it) => {
        const children = Array.isArray(it.children) ? filterItems(it.children) : undefined;
        return children ? { ...it, children } : it;
      });
  return config
    .map((section) => ({ ...section, items: Array.isArray(section.items) ? filterItems(section.items) : [] }))
    .filter((section) => section.items.length > 0);
}

/** Drop later sidebar links that point to the same route (keeps first occurrence). */
function filterDuplicatePaths(config) {
  const seenPaths = new Set();

  const visit = (items) => {
    const out = [];
    for (const it of items) {
      if (Array.isArray(it.children) && it.children.length) {
        const children = visit(it.children);
        if (children.length) out.push({ ...it, children });
        continue;
      }
      const path = it.path ? String(it.path).replace(/\/+$/, "") || "/" : null;
      if (!path) {
        out.push(it);
        continue;
      }
      if (seenPaths.has(path)) continue;
      seenPaths.add(path);
      out.push(it);
    }
    return out;
  };

  return config
    .map((section) => ({ ...section, items: visit(section.items || []) }))
    .filter((section) => (section.items || []).length > 0);
}

/** Filter the tree by a search query — keeps parents of matching descendants.
 *  Matches against label, path, and (for whole sections) the section name. */
function searchSidebar(config, query) {
  if (!query) return config;
  const q = query.trim().toLowerCase();
  if (!q) return config;

  const itemMatches = (it) => {
    if ((it.label || "").toLowerCase().includes(q)) return true;
    if ((it.path || "").toLowerCase().includes(q)) return true;
    if ((it.key || "").toLowerCase().includes(q)) return true;
    return false;
  };

  const filterItems = (items) => {
    const out = [];
    for (const it of items) {
      const childMatches = Array.isArray(it.children) ? filterItems(it.children) : [];
      if (itemMatches(it) || childMatches.length) {
        out.push(childMatches.length ? { ...it, children: childMatches } : it);
      }
    }
    return out;
  };

  return config
    .map((section) => {
      const sectionMatches = (section.section || "").toLowerCase().includes(q);
      const items = sectionMatches ? section.items || [] : filterItems(section.items || []);
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

/** Walk the tree and return the first leaf item with a path. */
function findFirstLeafPath(config) {
  for (const section of config) {
    const stack = [...(section.items || [])];
    while (stack.length) {
      const it = stack.shift();
      if (it.path) return it.path;
      if (Array.isArray(it.children) && it.children.length) {
        stack.unshift(...it.children);
      }
    }
  }
  return null;
}

/** Match sidebar link paths including optional ?query= and #hash fragments. */
function sidebarPathMatches(itemPath, pathname, search = "", hash = "") {
  if (!itemPath) return false;
  const raw = String(itemPath);

  // Strip hash fragment from itemPath first
  const hashIdx = raw.indexOf("#");
  const itemHash = hashIdx >= 0 ? raw.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? raw.slice(0, hashIdx) : raw;

  const qIdx = withoutHash.indexOf("?");
  const itemBase = (qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash).replace(/\/+$/, "") || "/";
  const normPath = String(pathname || "").replace(/\/+$/, "") || "/";

  if (itemBase === "/employees") {
    if (
      normPath === "/org-hierarchy" ||
      normPath.startsWith("/org-hierarchy/") ||
      normPath === "/org-structure" ||
      normPath.startsWith("/org-structure/") ||
      normPath === "/employee-lifecycle" ||
      normPath.startsWith("/employee-lifecycle/")
    ) {
      return true;
    }
  }

  if (normPath !== itemBase && !normPath.startsWith(`${itemBase}/`)) return false;

  // Check query params
  const itemQuery = qIdx >= 0 ? withoutHash.slice(qIdx + 1) : "";

  // Leave "Dashboard" is the tab-less /leave view; sibling tabs use ?tab=
  if (itemBase === "/leave" && !itemQuery) {
    const tab = new URLSearchParams(search || "").get("tab");
    if (tab && tab !== "dashboard") return false;
  }
  if (itemQuery) {
    const itemParams = new URLSearchParams(itemQuery);
    const locParams = new URLSearchParams(search || "");
    for (const [key, val] of itemParams.entries()) {
      if (locParams.get(key) !== val) return false;
    }
  }

  // Check hash fragment (only when the item specifies one)
  if (itemHash) {
    const locHash = String(hash || "");
    if (locHash !== itemHash) return false;
  }

  return true;
}

/** Walk every leaf path and the route they map to. */
function collectAccordionKeysForPath(config, pathname, search = "", hash = "") {
  const open = {};
  const walk = (items, parentKey) => {
    for (const it of items) {
      const selfActive = sidebarPathMatches(it.path, pathname, search, hash);
      let matched = selfActive;
      if (Array.isArray(it.children) && it.children.length) {
        const inside = walk(it.children, it.key);
        if (inside) {
          open[it.key] = true;
          matched = true;
        }
      }
      if (matched) {
        if (parentKey) open[parentKey] = true;
        return true;
      }
    }
    return false;
  };
  for (const section of config) walk(section.items || [], null);
  return open;
}

const LS_COLLAPSED = "sb:collapsed";
const LS_OPEN_MENUS = "sb:openMenus";

const safeStorage = {
  get(key, fallback) {
    try {
      const v = localStorage.getItem(key);
      return v == null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
};

validateSidebar(SIDEBAR_CONFIG);

/* ===
   Component
=== */
export default function Sidebar({ mobileOpen, isMobile, onCollapsedChange, onRequestClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, pageAccess } = useContext(AuthContext);
  const { isEnabled, loaded: featuresLoaded } = useFeatures();
  const shiftNavEnabled = useAttendanceShiftNav();
  const { enabled: otHoursAllocationEnabled } = useOtHoursAllocationNav();

  // Saved desktop preference. The mobile drawer has no expand button, so it is
  // always shown expanded — otherwise a collapse saved on desktop leaves the
  // drawer stuck as an icon rail where section accordions (e.g. Reports) can't open.
  const [collapsedPref, setCollapsed] = useState(() => safeStorage.get(LS_COLLAPSED, false));
  const collapsed = collapsedPref && !isMobile;
  const [openMenus, setOpenMenus] = useState(() => safeStorage.get(LS_OPEN_MENUS, {}));
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  const isSettingsRoute = isSettingsPath(location.pathname);
  const isSettingsHome = isSettingsRoute && location.pathname === "/settings" && !location.search.includes("tab=");

  const settingsActiveCatId = useMemo(() => {
    if (!isSettingsRoute || isSettingsHome) return null;
    for (const cat of SETTINGS_CATEGORIES) {
      for (const item of cat.items) {
        if (sidebarPathMatches(item.path, location.pathname, location.search, location.hash)) {
          return cat.id;
        }
      }
    }
    return null;
  }, [isSettingsHome, isSettingsRoute, location.pathname, location.search, location.hash]);

  const [settingsExpanded, setSettingsExpanded] = useState(() =>
    settingsActiveCatId ? { [settingsActiveCatId]: true } : {}
  );

  useEffect(() => {
    if (settingsActiveCatId) {
      setSettingsExpanded({ [settingsActiveCatId]: true });
    }
  }, [settingsActiveCatId]);

  const settingsVisibleCats = useMemo(() => {
    if (!isSettingsRoute || isSettingsHome) return [];
    const nr = normalizeRole(role);
    return SETTINGS_CATEGORIES.filter((cat) => {
      const catRoles = cat.items.flatMap((it) => it.roles || []);
      if (!catRoles.length) return true;
      return catRoles.some((r) => hasRole(nr, [r]));
    });
  }, [isSettingsRoute, isSettingsHome, role]);

  const toggleSettingsCat = useCallback((catId) => {
    setSettingsExpanded((prev) => (prev[catId] ? {} : { [catId]: true }));
  }, []);

  const handleSettingsBack = useCallback(() => {
    clearPipelineState();
    navigate(consumeSettingsReturnPath(), { replace: true });
  }, [navigate]);

  // Build role/template-filtered config
  const baseConfig = useMemo(() => {
    const normalizedRole = normalizeRole(role);
    const useTemplateAccess = Boolean(pageAccess?.page_keys?.length);

    const roleAllowed = (allowed) => {
      if (!normalizedRole) return false;
      if (!Array.isArray(allowed) || !allowed.length) return true;
      return hasRole(normalizedRole, allowed);
    };

    const applyItemRoles = (items) =>
      items
        .filter((it) => roleAllowed(it.roles))
        .filter((it) => !it.requiresShiftAttendance || shiftNavEnabled)
        .filter((it) => !it.requiresOtHoursAllocation || otHoursAllocationEnabled)
        .filter((it) => {
          if (!it.feature) return true;
          if (!featuresLoaded) return true;
          return isEnabled(it.feature);
        })
        .map((it) => {
          const children = Array.isArray(it.children) ? applyItemRoles(it.children) : undefined;
          return children ? { ...it, children } : it;
        })
        .filter((it) => !it.children || it.children.length > 0);

    const sourceConfig =
      normalizedRole === R.EMPLOYEE ? EMPLOYEE_SIDEBAR_CONFIG : SIDEBAR_CONFIG;

    const roleFilteredSections = sourceConfig
      .filter((section) => roleAllowed(section.roles))
      .filter((section) => {
        if (!section.feature) return true;
        if (!featuresLoaded) return true;
        return isEnabled(section.feature);
      });

    const filtered = useTemplateAccess
      ? roleFilteredSections
          .map((section) => ({
            ...section,
            items: filterSidebarByPageAccess(
              applyItemRoles(section.items || []),
              pageAccess,
              normalizedRole
            ),
          }))
          .filter((section) => (section.items || []).length > 0)
      : roleFilteredSections.map((section) => ({
          ...section,
          items: applyItemRoles(section.items || []),
        }));

    return filterDuplicatePaths(filterUniqueSidebar(filtered));
  }, [role, pageAccess, shiftNavEnabled, otHoursAllocationEnabled, featuresLoaded, isEnabled]);

  // Apply search filter on top
  const config = useMemo(() => searchSidebar(baseConfig, query), [baseConfig, query]);

  // First matching leaf path — used to jump on Enter
  const firstMatchPath = useMemo(
    () => (query.trim() ? findFirstLeafPath(config) : null),
    [config, query]
  );

  // Persist + propagate collapse
  useEffect(() => {
    safeStorage.set(LS_COLLAPSED, collapsedPref);
    onCollapsedChange?.(collapsed);
  }, [collapsedPref, collapsed, onCollapsedChange]);

  // Persist openMenus
  useEffect(() => {
    safeStorage.set(LS_OPEN_MENUS, openMenus);
  }, [openMenus]);

  // Auto-open parents of the active route OR matching search items
  useEffect(() => {
    const next = collectAccordionKeysForPath(config, location.pathname, location.search, location.hash);

    if (query.trim()) {
      // While searching: expand every accordion that has matching items (merge to preserve state)
      const walk = (items) => {
        for (const it of items) {
          if (Array.isArray(it.children) && it.children.length) {
            next[it.key] = true;
            walk(it.children);
          }
        }
      };
      for (const section of config) walk(section.items || []);
      setOpenMenus((prev) => ({ ...prev, ...next }));
    } else {
      // On route change: replace entirely so unrelated sections collapse automatically
      setOpenMenus(next);
    }
  }, [location.pathname, location.search, location.hash, config, query]);

  // Keyboard: "/" focuses search; "Esc" clears
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
        searchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCollapse = useCallback(
    (val) => {
      setCollapsed(val);
      if (val) setOpenMenus({});
    },
    []
  );

  // Map every accordion key → the Set of its sibling accordion keys (same parent level).
  // Depth-0 accordions across all sections are treated as siblings of each other.
  const siblingMap = useMemo(() => {
    const map = new Map();

    const registerGroup = (items) => {
      const siblingKeys = items
        .filter((it) => Array.isArray(it.children) && it.children.length > 0)
        .map((it) => it.key);
      for (const key of siblingKeys) {
        map.set(key, new Set(siblingKeys.filter((k) => k !== key)));
      }
      for (const it of items) {
        if (Array.isArray(it.children) && it.children.length > 0) {
          registerGroup(it.children);
        }
      }
    };

    // Treat depth-0 items across ALL sections as one sibling group
    const allRootItems = config.flatMap((s) => s.items || []);
    registerGroup(allRootItems);

    return map;
  }, [config]);

  // Single accordion toggle: close sibling accordions at the same level, then toggle this one
  const toggleAccordion = useCallback((key) => {
    setOpenMenus((prev) => {
      const isOpen = !!prev[key];
      const next = { ...prev };
      const siblings = siblingMap.get(key) || new Set();
      for (const sibKey of siblings) {
        next[sibKey] = false;
      }
      next[key] = !isOpen;
      return next;
    });
  }, [siblingMap]);

  // Section/item totals (for badge in collapsed mode of each section)
  const totalVisibleItems = useMemo(() => {
    let n = 0;
    const walk = (items) => {
      for (const it of items) {
        if (it.path) n += 1;
        if (Array.isArray(it.children)) walk(it.children);
      }
    };
    for (const s of config) walk(s.items || []);
    return n;
  }, [config]);

  return (
    <>
      <style>{SIDEBAR_CSS}</style>

      <aside
        className={`sb ${mobileOpen ? "open" : ""} ${collapsed ? "collapsed" : ""}`}
        aria-label="Primary navigation"
      >
        <div className="sb-ambient" aria-hidden="true">
          <div className="sb-orb sb-orb-1" />
          <div className="sb-orb sb-orb-2" />
          <div className="sb-orb sb-orb-3" />
          <div className="sb-shine" />
        </div>

        {/* -- Brand Header -- */}
        <div className="sb-header">
          <div className="sb-brand">
            <div className="sb-logo-wrap">
              <img src={logo1} alt="Brixigo" className="sb-logo-img" />
            </div>
            {!collapsed && (
              <div className="sb-brand-text">
                <div className="sb-brand-name">Brixigo</div>
                <div className="sb-brand-sub">HR & Payroll Suite</div>
              </div>
            )}
          </div>

          {isMobile ? (
            <button
              className="sb-icon-btn"
              onClick={() => onRequestClose?.()}
              title="Close"
              aria-label="Close menu"
              type="button"
            >
              <X size={16} />
            </button>
          ) : (
            <button
              className="sb-icon-btn"
              onClick={() => handleCollapse(!collapsed)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              type="button"
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          )}
        </div>

        {/* -- Search (hidden in settings sub-page mode) -- */}
        {!collapsed && (!isSettingsRoute || isSettingsHome) && (
          <div className="sb-search-wrap">
            <Search size={14} className="sb-search-icon" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && firstMatchPath) {
                  e.preventDefault();
                  navigate(firstMatchPath);
                  setQuery("");
                  searchRef.current?.blur();
                  if (mobileOpen) onRequestClose?.();
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const firstLink = document
                    .querySelector(".sb-nav .sb-item, .sb-nav .sb-acc-trigger");
                  firstLink?.focus?.();
                }
              }}
              placeholder="Search menu..."
              className="sb-search-input"
              aria-label="Search navigation"
              autoComplete="off"
              spellCheck={false}
            />
            {query ? (
              <button
                type="button"
                className="sb-search-clear"
                onClick={() => {
                  setQuery("");
                  searchRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            ) : (
              <kbd className="sb-kbd" title="Press / to focus">/</kbd>
            )}
          </div>
        )}

        {/* -- Navigation -- */}
        <nav
          className="sb-nav"
          onClick={(e) => {
            const a = e.target?.closest?.("a");
            if (a && mobileOpen) onRequestClose?.();
          }}
        >
          {isSettingsRoute && !isSettingsHome ? (
            <>
              {!collapsed && (
                <button type="button" className="sb-settings-back" onClick={handleSettingsBack}>
                  <ArrowLeft size={14} />
                  <span>Back to Main Sidebar</span>
                </button>
              )}
              {collapsed && (
                <div className="sb-tooltip-wrap">
                  <button type="button" className="sb-item" onClick={handleSettingsBack} style={{ justifyContent: 'center' }}>
                    <div className="sb-icon ic-slate"><ArrowLeft size={14} /></div>
                  </button>
                  <div className="sb-tooltip">Back to Main Sidebar</div>
                </div>
              )}
              {!collapsed && <div className="sb-section">Settings</div>}
              {settingsVisibleCats.map((cat, idx) => {
                const isExpanded = !!settingsExpanded[cat.id];
                const nr = normalizeRole(role);
                const items = filterSettingsHubItems(cat.items).filter((item) => {
                  const roles = item.roles || [];
                  if (!roles.length) return true;
                  return roles.some((r) => hasRole(nr, [r]));
                });
                if (!items.length) return null;
                const CatIcon = (typeof items[0]?.icon === "function" || items[0]?.icon?.$$typeof) ? items[0].icon : Settings;
                return (
                  <div className="sb-section-block" key={cat.id}>
                    {idx > 0 && !collapsed && <div className="sb-divider" />}
                    <div className="sb-tooltip-wrap">
                      <button
                        type="button"
                        className={`sb-acc-trigger ${isExpanded ? "open" : ""}`}
                        onClick={() => !collapsed && toggleSettingsCat(cat.id)}
                        aria-expanded={isExpanded}
                      >
                        <div className="sb-icon ic-slate">
                          <CatIcon size={14} />
                        </div>
                        <span className="sb-label">{cat.label}</span>
                        {!collapsed && <span className="sb-count">{items.length}</span>}
                        <ChevronDown size={12} className={`sb-chevron ${isExpanded ? "open" : ""}`} />
                      </button>
                      {collapsed && <div className="sb-tooltip">{cat.label}</div>}
                    </div>
                    {isExpanded && !collapsed && (
                      <div className="sb-acc-inner">
                        {items.map((item) => {
                          const ItemIcon = (typeof item.icon === "function" || item.icon?.$$typeof) ? item.icon : FileText;
                          return (
                            <div className="sb-tooltip-wrap" key={item.id}>
                              <NavLink
                                to={item.path}
                                className={() => {
                                  const active = sidebarPathMatches(item.path, location.pathname, location.search, location.hash);
                                  return `sb-item sb-item-sub ${active ? "active" : ""}`;
                                }}
                                style={{ paddingLeft: 24 }}
                              >
                                <div className="sb-icon ic-blue" style={{ width: 24, height: 24, marginTop: 1 }}>
                                  <ItemIcon size={14} />
                                </div>
                                <span className="sb-label">{item.label}</span>
                              </NavLink>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {config.length === 0 && (
                <div className="sb-empty">
                  <Sparkles size={20} />
                  <div className="sb-empty-title">No results</div>
                  <div className="sb-empty-sub">Try a different keyword</div>
                </div>
              )}

              {config.map((section, idx) => (
                <div className="sb-section-block" key={section.section}>
                  {idx > 0 && !collapsed && <div className="sb-divider" />}
                  <div className="sb-section">{section.section}</div>

                  {(section.items || []).map((item) => (
                    <SidebarNode
                      key={item.key}
                      item={item}
                      collapsed={collapsed}
                      openMenus={openMenus}
                      onToggle={toggleAccordion}
                      query={query}
                      depth={0}
                      onItemClick={() => handleCollapse(true)}
                    />
                  ))}
                </div>
              ))}

              {!collapsed && totalVisibleItems > 0 && (
                <div className="sb-stat">{totalVisibleItems} pages available</div>
              )}
            </>
          )}
        </nav>
      </aside>
    </>
  );
}

/* -- Recursive node -- */
function SidebarNode({ item, collapsed, openMenus, onToggle, query, depth, onItemClick }) {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  if (hasChildren) {
    return (
      <SbAccordion
        item={item}
        open={!!openMenus[item.key] && !collapsed}
        onAccordionToggle={() => !collapsed && onToggle(item.key)}
        collapsed={collapsed}
        openMenus={openMenus}
        onToggle={onToggle}
        query={query}
        depth={depth}
        onItemClick={onItemClick}
      />
    );
  }

  return (
    <SbItem
      to={item.path}
      label={item.label}
      iconName={item.icon}
      color={item.color}
      collapsed={collapsed}
      query={query}
      depth={depth}
      onClick={item.collapseOnClick ? onItemClick : undefined}
    />
  );
}

/* -- Leaf nav item -- */
function SbItem({ to, label, iconName, color, collapsed, query, depth, onClick }) {
  const Icon = ICONS[iconName] || FileText;
  const location = useLocation();
  return (
    <div className="sb-tooltip-wrap">
      <NavLink
        to={to}
        onClick={onClick}
        className={() => {
          const active = sidebarPathMatches(to, location.pathname, location.search, location.hash);
          return `sb-item ${active ? "active" : ""} ${depth > 0 ? "sb-item-sub" : ""}`;
        }}
        style={depth > 0 ? { paddingLeft: 12 + depth * 12 } : undefined}
      >
        <div className={`sb-icon ${color || "ic-slate"}`}>
          <Icon size={14} />
        </div>
        <span className="sb-label">{highlight(label, query)}</span>
      </NavLink>
      {collapsed && <div className="sb-tooltip">{label}</div>}
    </div>
  );
}

/* -- Accordion -- */
function SbAccordion({ item, open, onAccordionToggle, collapsed, openMenus, onToggle, query, depth, onItemClick }) {
  const Icon = ICONS[item.icon] || Layers;
  const childCount = Array.isArray(item.children) ? item.children.length : 0;

  return (
    <div className="sb-tooltip-wrap">
      <button
        type="button"
        className={`sb-acc-trigger ${open ? "open" : ""} ${depth > 0 ? "sb-acc-sub" : ""}`}
        onClick={onAccordionToggle}
        aria-expanded={open}
        style={depth > 0 ? { paddingLeft: 10 + depth * 12 } : undefined}
      >
        <div className={`sb-icon ${item.color || "ic-slate"}`}>
          <Icon size={14} />
        </div>
        <span className="sb-label">{highlight(item.label, query)}</span>
        {!collapsed && <span className="sb-count">{childCount}</span>}
        <ChevronDown size={12} className={`sb-chevron ${open ? "open" : ""}`} />
      </button>

      {open && !collapsed && (
        <div className="sb-acc-inner">
          {(item.children || []).map((child) => (
            <SidebarNode
              key={child.key}
              item={child}
              collapsed={collapsed}
              openMenus={openMenus}
              onToggle={onToggle}
              query={query}
              depth={depth + 1}
              onItemClick={onItemClick}
            />
          ))}
        </div>
      )}
      {collapsed && <div className="sb-tooltip">{item.label}</div>}
    </div>
  );
}

/* -- Highlight matched query inside a label -- */
function highlight(label, query) {
  if (!query || !query.trim()) return label;
  const q = query.trim();
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="sb-mark">{label.slice(idx, idx + q.length)}</mark>
      {label.slice(idx + q.length)}
    </>
  );
}

/* ===
   Styles — Income Tax portal interactions + previous navy background (#1a437d)
=== */
const SIDEBAR_CSS = `
:root {
  --sb-shell-navy: #1a437d;
  --sb-purple: #FFFFFF;
  --sb-purple-dk: #FFFFFF;
  --sb-purple-soft: rgba(255, 255, 255, 0.12);
  --sb-purple-hover: rgba(255, 255, 255, 0.08);
  --sb-bg: #1a437d;
  --sb-bg-soft: rgba(255, 255, 255, 0.06);
  --sb-bg-elevated: rgba(255, 255, 255, 0.1);
  --sb-border: rgba(255, 255, 255, 0.12);
  --sb-border-glow: rgba(255, 255, 255, 0.18);
  --sb-text: #FFFFFF;
  --sb-text-sub: rgba(255, 255, 255, 0.85);
  --sb-text-light: #FFFFFF;
  --sb-muted: rgba(255, 255, 255, 0.85);
  --sb-faint: rgba(255, 255, 255, 0.85);
  --sb-hover: rgba(255, 255, 255, 0.08);
  --sb-hover-solid: rgba(255, 255, 255, 0.12);
  --sb-accent: #FFFFFF;
  --sb-accent-soft: rgba(255, 255, 255, 0.12);
  --sb-accent-strong: #FFFFFF;
  --sb-glow-blue: transparent;
  --sb-glow-cyan: transparent;
}

.sb {
  height: 100vh;
  width: var(--sidebar-width, 248px);
  background: #1a437d;
  color: #FFFFFF;
  border-right: none;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  transition: width 280ms cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  position: relative;
  isolation: isolate;
}
.sb::before { display: none; }
.sb > *:not(.sb-ambient) { position: relative; z-index: 1; }
.sb.collapsed { width: 72px; }

/* -- Ambient glow orbs (hidden on light sidebar) -- */
.sb-ambient { display: none; }
.sb-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(56px);
  opacity: 0.4;
  will-change: transform, opacity;
}
.sb-orb-1 {
  width: 180px; height: 180px;
  top: -40px; left: -50px;
  background: radial-gradient(circle, rgba(66, 165, 245, 0.28) 0%, transparent 70%);
  animation: sbOrbFloat1 12s ease-in-out infinite;
}
.sb-orb-2 {
  width: 140px; height: 140px;
  bottom: 15%; right: -40px;
  background: radial-gradient(circle, rgba(0, 242, 195, 0.14) 0%, transparent 70%);
  animation: sbOrbFloat2 14s ease-in-out infinite;
}
.sb-orb-3 {
  width: 100px; height: 100px;
  top: 45%; left: 20%;
  background: radial-gradient(circle, rgba(79, 195, 247, 0.22) 0%, transparent 70%);
  animation: sbOrbFloat3 10s ease-in-out infinite;
}
.sb-shine {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 25%,
    rgba(147, 197, 253, 0.45) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: sbShineSweep 6s ease-in-out infinite;
}
@keyframes sbOrbFloat1 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.35; }
  50% { transform: translate(20px, 30px) scale(1.1); opacity: 0.5; }
}
@keyframes sbOrbFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
  50% { transform: translate(-15px, -25px) scale(1.15); opacity: 0.45; }
}
@keyframes sbOrbFloat3 {
  0%, 100% { transform: translate(0, 0); opacity: 0.25; }
  50% { transform: translate(10px, -20px); opacity: 0.4; }
}
@keyframes sbShineSweep {
  0%, 100% { background-position: 200% 0; opacity: 0.5; }
  50% { background-position: -100% 0; opacity: 0.8; }
}

/* -- Header (logo strip — same navy as sidebar) -- */
.sb-header {
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
  background: #1a437d;
  color: #FFFFFF;
  position: relative;
}
.sb-header::after {
  display: none;
}

.sb-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  min-width: 0;
}
.sb-logo-wrap {
  width: 38px; height: 38px;
  border-radius: 11px;
  background: #ffffff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(58, 77, 98, 0.2);
  overflow: hidden;
  border: none;
}
.sb-logo-img { height: 34px; width: 34px; object-fit: contain; }

.sb-brand-text { overflow: hidden; white-space: nowrap; min-width: 0; }
.sb-brand-name {
  font-weight: 700; font-size: 14px;
  color: #ffffff; letter-spacing: -0.3px; line-height: 1.1;
}
.sb-brand-sub {
  font-size: 10px; color: rgba(255, 255, 255, 0.85);
  font-weight: 500; margin-top: 2px; letter-spacing: 0.2px;
}

.sb-icon-btn {
  width: 28px; height: 28px;
  border-radius: var(--r-lg, 12px);
  border: 1px solid rgba(255, 255, 255, 0.28);
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  color: #ffffff;
  transition: all 180ms cubic-bezier(0.22, 1, 0.36, 1);
  flex-shrink: 0; outline: none;
  box-shadow: none;
}
.sb-icon-btn:hover {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.sb-icon-btn:active { transform: scale(0.96); }

/* -- Search -- */
.sb-search-wrap {
  position: relative;
  padding: 10px 12px 4px;
  display: flex;
  align-items: center;
}
.sb-search-icon {
  position: absolute;
  left: 22px;
  color: rgba(255, 255, 255, 0.85);
  pointer-events: none;
  z-index: 1;
}
.sb-search-input {
  width: 100%;
  height: 34px;
  padding: 0 36px 0 32px;
  border-radius: 10px;
  border: 1px solid var(--sb-border);
  background: #ffffff;
  color: #1a437d;
  font-size: 12.5px;
  font-weight: 500;
  outline: none;
  transition: all 180ms cubic-bezier(0.22, 1, 0.36, 1);
  font-family: inherit;
  box-shadow: inset 0 1px 2px rgba(108, 114, 147, 0.06);
  caret-color: #1caad9;
}
.sb-search-input::placeholder { color: #94a3b8; font-weight: 400; }
.sb-search-input:focus {
  background: #ffffff;
  border-color: var(--sb-purple);
  box-shadow:
    0 0 0 3px rgba(28, 170, 217, 0.12),
    0 4px 14px rgba(27, 43, 58, 0.08);
}
.sb-search-clear {
  position: absolute;
  right: 20px;
  width: 18px; height: 18px;
  border: none;
  background: #e2e8f0;
  color: #64748b;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 150ms ease;
  z-index: 1;
}
.sb-search-clear:hover {
  background: var(--sb-purple);
  color: #ffffff;
}
.sb-kbd {
  position: absolute;
  right: 20px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.55);
  box-shadow: none;
}

/* -- Nav scroll -- */
.sb-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 8px 16px;
  background: #1a437d;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
}
.sb-nav::-webkit-scrollbar { width: 6px; }
.sb-nav::-webkit-scrollbar-track { background: transparent; }
.sb-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  transition: background 180ms ease;
}
.sb-nav:hover::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.25); }

/* -- Section -- */
.sb-section-block {
  animation: sbFadeUp 240ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes sbFadeUp {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.sb-divider {
  height: 1px;
  margin: 10px 12px 0;
  background: rgba(255, 255, 255, 0.12);
}
.sb-section {
  font-size: 9.5px;
  font-weight: 700;
  color: var(--sb-faint);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  padding: 0 10px;
  margin: 14px 0 6px;
}
.sb.collapsed .sb-section { opacity: 0; height: 0; margin: 6px 0; overflow: hidden; }

/* -- Item -- */
.sb-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  color: #FFFFFF;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;
  position: relative;
  white-space: normal;
  margin-bottom: 2px;
  isolation: isolate;
  border-left: 4px solid transparent;
  border-top: none;
  border-right: none;
  border-bottom: none;
  box-shadow: none;
}
.sb-item::before {
  display: none;
}
.sb-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
  transform: none;
  box-shadow: none;
}
.sb-item.active {
  background: rgba(255, 255, 255, 0.12);
  color: #FFFFFF;
  font-weight: 600;
  border-left: 4px solid #FFFFFF;
  box-shadow: none;
}
.sb-item-sub {
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  padding: 7px 8px 7px 10px;
  align-items: flex-start;
}
.sb-item-sub:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
}
.sb-item-sub.active {
  color: #FFFFFF;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.12);
  border-left: 4px solid #FFFFFF;
  border-radius: 10px;
}

.sb-label {
  flex: 1;
  min-width: 0;
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}

/* Highlighted search match */
.sb-mark {
  background: linear-gradient(180deg, transparent 55%, rgba(253, 224, 71, 0.55) 55%);
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}

/* -- Icon chip -- */
.sb-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 200ms ease;
}
.sb-icon svg {
  color: inherit;
  stroke: currentColor;
}
.sb-item:hover .sb-icon,
.sb-acc-trigger:hover .sb-icon {
  transform: scale(1.06) rotate(-2deg);
}
.sb-item.active .sb-icon {
  box-shadow: none;
}
.sb-item-sub .sb-icon {
  width: 24px;
  height: 24px;
  margin-top: 1px;
}

/* -- Accordion trigger -- */
.sb-acc-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  border-left: 4px solid transparent;
  background: transparent;
  width: 100%;
  font-size: 13px;
  color: #FFFFFF;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.25s ease;
  margin-bottom: 2px;
  outline: none;
  white-space: normal;
  text-align: left;
  position: relative;
}
.sb-acc-trigger:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
  box-shadow: none;
}
.sb-acc-trigger.open {
  color: #FFFFFF;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}
.sb-acc-trigger .sb-icon svg {
  color: inherit;
  stroke: currentColor;
}
.sb-acc-trigger .sb-chevron {
  color: rgba(255, 255, 255, 0.85);
  flex-shrink: 0;
}
.sb-acc-sub {
  color: rgba(255, 255, 255, 0.88);
  font-weight: 500;
  font-size: 12.5px;
}
.sb-acc-sub:hover,
.sb-acc-sub.open { color: #FFFFFF; }

.sb-count {
  font-size: 10px;
  font-weight: 600;
  color: var(--sb-muted);
  background: var(--sb-bg);
  border: 1px solid var(--sb-border);
  border-radius: 999px;
  padding: 1px 7px;
  min-width: 18px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(108, 114, 147, 0.06);
}
.sb-acc-trigger.open .sb-count {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #FFFFFF;
}

.sb-chevron {
  margin-left: 4px;
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  color: var(--sb-faint);
  flex-shrink: 0;
}
.sb-chevron.open { transform: rotate(180deg); color: var(--sb-accent-strong); }
.sb.collapsed .sb-chevron,
.sb.collapsed .sb-count { display: none; }

.sb-acc-inner {
  padding-left: 6px;
  margin-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  background: #1a437d;
  overflow: hidden;
  animation: sbAccordion 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes sbAccordion {
  from { opacity: 0; transform: translateY(-4px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 1200px; }
}

/* -- Tooltip (collapsed mode) -- */
.sb-tooltip-wrap { position: relative; }
.sb.collapsed .sb-tooltip-wrap:hover .sb-tooltip {
  opacity: 1;
  transform: translate(0, -50%);
  pointer-events: auto;
}
.sb-tooltip {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translate(-6px, -50%);
  background: #0f172a;
  color: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 11px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: all 160ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 300;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.15);
}
.sb-tooltip::before {
  content: "";
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-right-color: #0f172a;
}

/* -- Collapsed visuals -- */
.sb.collapsed .sb-brand-text,
.sb.collapsed .sb-item .sb-label,
.sb.collapsed .sb-acc-trigger .sb-label,
.sb.collapsed .sb-acc-inner,
.sb.collapsed .sb-search-wrap,
.sb.collapsed .sb-stat,
.sb.collapsed .sb-divider { display: none; }
.sb.collapsed .sb-item,
.sb.collapsed .sb-acc-trigger {
  justify-content: center;
  padding: 9px;
}
.sb.collapsed .sb-item.active .sb-icon {
  box-shadow: none;
}
.sb.collapsed .sb-item::before { display: none; }
.sb.collapsed .sb-item.active {
  background: rgba(255, 255, 255, 0.12);
  border-left: 4px solid #FFFFFF;
  position: relative;
}
.sb.collapsed .sb-item.active::after {
  display: none;
}

/* -- Empty & stat -- */
.sb-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--sb-faint);
  animation: sbFadeUp 240ms ease both;
}
.sb-empty-title {
  margin-top: 10px;
  font-weight: 700;
  font-size: 13px;
  color: var(--sb-text-light);
}
.sb-empty-sub { font-size: 11px; margin-top: 4px; color: var(--sb-faint); }

.sb-stat {
  padding: 14px 12px 4px;
  font-size: 10px;
  color: var(--sb-faint);
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

/* -- Icon color tokens — light chips + tinted icons (previous look) -- */
.ic-blue   { background: #e8f7fc; color: #1caad9; }
.ic-green  { background: #e8f7fc; color: #1caad9; }
.ic-teal   { background: #e8f7fc; color: #1caad9; }
.ic-amber  { background: #fff3e8; color: #f69433; }
.ic-red    { background: #fee2e2; color: #dc2626; }
.ic-purple { background: #e8f7fc; color: #1caad9; }
.ic-slate  { background: #eef2f7; color: #64748b; }

.sb-item.active .ic-blue,
.sb-item.active .ic-green,
.sb-item.active .ic-teal,
.sb-item.active .ic-purple {
  background: #dff3fa;
  color: #1594b8;
}
.sb-item.active .ic-amber {
  background: #ffe8d4;
  color: #e07d1a;
}
.sb-item.active .ic-red {
  background: #fecaca;
  color: #b91c1c;
}
.sb-item.active .ic-slate {
  background: #e2e8f0;
  color: #475569;
}

/* -- Responsive -- */
@media (max-width: 1024px) {
  .sb { width: 260px; }
}
@media (max-width: 768px) {
  .sb {
    width: 280px;
    box-shadow: 8px 0 32px rgba(108, 114, 147, 0.18);
  }
  .sb-header { height: 60px; }
}

@media (prefers-reduced-motion: reduce) {
  .sb, .sb-item, .sb-icon, .sb-chevron, .sb-acc-inner, .sb-section-block,
  .sb-orb, .sb-shine {
    transition: none !important;
    animation: none !important;
  }
}

/* -- Settings mode back button -- */
.sb-settings-back {
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 4px);
  margin: 4px 2px 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  font-family: inherit;
  text-align: left;
}
.sb-settings-back:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.28);
}
.sb-settings-back:active {
  transform: scale(0.98);
}
.sb-settings-back svg {
  flex-shrink: 0;
  transition: transform 0.15s;
}
.sb-settings-back:hover svg {
  transform: translateX(-2px);
}
`;
